import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useAppContext } from '../context';
import { useGetSubscriptionPlans } from '../subscription-plans/useGetSubscriptionPlans';
import { useFieldArray } from 'react-hook-form';
import { SelectChangeEvent } from '@mui/material/Select';
import { SpecialUser, User } from '../../@types';
import { useAddSpecialUserByAdmin } from '../../services/admin';
import { useSnackbar } from 'notistack';
import { axiosInstance } from '../../utils';
import { useState, useEffect } from 'react';

interface FormValues {
  users: SpecialUser[];
}

export const useAdminPage = () => {
  const FormSchema = Yup.object().shape({
    users: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string(),
          name: Yup.string()
            .trim()
            .required('Name is required')
            .typeError('Name is required')
            .max(25, 'Name must be at most 25 characters'),
          email: Yup.string()
            .nullable()
            .email('Please enter a valid email address')
            .required('Email is required')
            .typeError('Email is required'),
          planId: Yup.string()
            .trim()
            .nullable()
            .required('Please select a plan')
            .typeError('Please select a plan'),
          extraCredits: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .notRequired()
            .typeError('Credits must be a number')
            .integer('Credits must be a number')
            .min(0, 'Credits must be at least 0'),
          endDate: Yup.date().nullable().notRequired().typeError('Invalid date'),
        })
      )
      .required('Users are required')
      .min(1, 'At least one user is required'),
  });

  const router = useRouter();
  const { userData, isUserDataLoading } = useAppContext();
  const { data: allSubscriptionPlans } = useGetSubscriptionPlans();
  const subscriptionPlanData = allSubscriptionPlans || []; // Hide Addon plans from the list
  const { mutate: mutateAddSpecialUserByAdmin, isLoading: isAddingUser } =
    useAddSpecialUserByAdmin();

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(FormSchema),
    defaultValues: {
      users: [
        {
          id: `user-${Date.now()}`,
          name: null,
          email: null,
          planId: null,
          extraCredits: null,
          endDate: null,
        },
      ],
    },
    mode: 'onChange',
  });

  const { handleSubmit, setValue, watch, reset } = formMethods;
  const { enqueueSnackbar } = useSnackbar();
  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'users',
  });

  const users = watch('users');
  const [userPlans, setUserPlans] = useState<Record<number, unknown[]>>({}); // key: user index, value: plans array

  useEffect(() => {
    if (router.query.email && typeof router.query.email === 'string') {
      setValue('users.0.email', router.query.email);
    }
  }, [router.query.email, setValue]);

  const handleRedirectToDashboard = () => {
    router.push('/dashboard');
  };

  const onSubmit = (data: FormValues) => {
    console.log('🚀 ~ onSubmit ~ data:', data);
    mutateAddSpecialUserByAdmin(
      { specialUsers: data.users },
      {
        onSuccess: () => {
          enqueueSnackbar('Details updated successfully!.', { variant: 'success' });
          reset({
            users: [
              {
                id: `user-${Date.now()}`,
                name: null,
                email: null,
                planId: null,
                extraCredits: null,
                endDate: null,
              },
            ],
          });
        },
        onError: (err: unknown) => {
          let message = 'An error occurred';
          if (typeof err === 'object' && err !== null) {
            // Check for axios error shape
            if (
              'response' in err &&
              typeof (err as { response?: { data?: { message?: string } } }).response?.data
                ?.message === 'string'
            ) {
              message = (err as { response: { data: { message: string } } }).response.data.message;
            } else if (
              'message' in err &&
              typeof (err as { message?: string }).message === 'string'
            ) {
              message = (err as { message: string }).message;
            }
          }
          enqueueSnackbar(message, { variant: 'error' });
        },
      }
    );
  };

  const addMoreUsers = () => {
    // Get the last user's planId and extraCredits if any users exist
    const lastUser = users.length > 0 ? users[users.length - 1] : null;
    const lastUserPlanId = lastUser ? lastUser.planId : null;
    const lastUserExtraCredits = lastUser ? lastUser.extraCredits : null;
    const lastUserEndDate = lastUser ? lastUser.endDate : null;

    const newUser = {
      id: `user-${Date.now()}`,
      name: null,
      email: null,
      planId: lastUserPlanId, // Use the same plan as the last user
      extraCredits: lastUserExtraCredits, // Forward extraCredits
      endDate: lastUserEndDate,
    };
    append(newUser);
  };

  const removeUser = (index: number) => {
    if (users.length === 1) {
      reset({
        users: [
          {
            id: `user-${Date.now()}`,
            name: null,
            email: null,
            planId: null,
            extraCredits: null,
          },
        ],
      });
      return;
    }
    remove(index);
  };

  const handlePlanChange = (event: SelectChangeEvent<string>, index: number) => {
    const planId = event.target.value as string;
    const plan = subscriptionPlanData?.find((plan) => plan.id === planId);
    if (plan) {
      setValue(`users.${index}.planId`, plan.id);
    }
  };

  const fetchUserPlan = async (userId: string) => {
    try {
      const res = await axiosInstance.get(`/api/payments/admin-user-plan/${userId}`);
      return res.data;
    } catch (error) {
      console.error('Error fetching user plan:', error);
      return null;
    }
  };

  const fetchSelectedUserPlan = async (index: number, user: User) => {
    try {
      const plans = await fetchUserPlan(user.id);
      if (plans) {
        setUserPlans((prev) => ({
          ...prev,
          [index]: plans.map((row: Record<string, unknown>) => {
            const plan = (row.plan ?? {}) as Record<string, unknown>;
            return {
              id: plan.id,
              planName: plan.planName || plan.name,
              interval: plan.interval,
              amount: plan.amount,
              credits: plan.credits,
              remainingCredits: row.remainingCredits,
            };
          }),
        }));
      }
    } catch (error) {
      console.error('Error setting selected user:', error);
      enqueueSnackbar('Failed to fetch user plans', { variant: 'error' });
    }
  };

  return {
    userData,
    isUserDataLoading,
    handleRedirectToDashboard,
    handleSubmit: handleSubmit(onSubmit),
    formMethods,
    subscriptionPlanData,
    addMoreUsers,
    removeUser,
    handlePlanChange,
    fields,
    users,
    isAddingUser,
    fetchSelectedUserPlan,
    userPlans,
  };
};
